import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FILM_STOCKS,
  NO_STOCK_ID,
  findPose,
  findPov,
  type Garment,
  type PipelineEvent,
  type Shot,
  type ShotMark,
} from '@studio/shared';

import { api, assetUrl, downloadUrl, subscribeToShoot, type ShootDetail, usd } from '../lib/api';
import { BackIcon, DownloadIcon, LoupeIcon } from '../components/Icons';
import { MarkStrip } from '../components/MarkStrip';
import { PublishScene, SentToGemini } from '../components/SentToGemini';
import { Sheet } from '../components/Sheet';
import { Skeleton } from '../components/Skeleton';
import { useDialog } from '../components/Dialog';
import { Display } from '../components/Display';
import { useToast } from '../components/Toaster';
import { useI18n, usePresetLabel, useT, type Translate } from '../lib/i18n';
import { useDateFormat } from '../lib/dates';
import { useErrorText } from '../lib/errors';
import { Dial } from './Wizard';

const ACTIVE = new Set(['queued', 'analyzing', 'styling', 'rendering', 'reviewing']);

export function ShootViewPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const { prompt } = useDialog();
  const t = useT();
  const { locale } = useI18n();
  const describe = useErrorText();
  const tp = usePresetLabel();
  const [detail, setDetail] = useState<ShootDetail | undefined>();
  const [events, setEvents] = useState<PipelineEvent[]>([]);
  const [progress, setProgress] = useState(0);
  const [inspecting, setInspecting] = useState<Shot | undefined>();
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const next = await api.getShoot(id);
      setDetail(next);
      setEvents(next.events);
      const last = next.events[next.events.length - 1];
      if (last) setProgress(last.progress);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('roll.notLoaded'));
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live progress. Events carry the updated shot, merged in place so finished
  // frames appear without refetching the whole roll.
  useEffect(() => {
    if (!id) return;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const unsubscribe = subscribeToShoot(id, (event) => {
      setEvents((prev) => [...prev, event]);
      setProgress(event.progress);

      if (event.shot) {
        const updated = event.shot;
        setDetail((prev) =>
          prev
            ? {
                ...prev,
                shots: prev.shots.some((s) => s.id === updated.id)
                  ? prev.shots.map((s) => (s.id === updated.id ? updated : s))
                  : [...prev.shots, updated],
              }
            : prev,
        );
      }
      if (event.shoot) setDetail((prev) => (prev ? { ...prev, shoot: event.shoot! } : prev));
      if (event.stage === 'done') refreshTimer = setTimeout(() => void load(), 400);
    });

    return () => {
      unsubscribe();
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [id, load]);

  /*
   * Marking is optimistic — the ring appears under your finger, and only rolls
   * back if the server rejects it.
   */
  const mark = useCallback(
    async (shotId: string, next: ShotMark | null) => {
      const apply = (value: ShotMark | undefined) =>
        setDetail((prev) =>
          prev
            ? {
                ...prev,
                shots: prev.shots.map((s) => (s.id === shotId ? { ...s, mark: value } : s)),
              }
            : prev,
        );
      const previous = detail?.shots.find((s) => s.id === shotId)?.mark;
      apply(next ?? undefined);
      setInspecting((prev) => (prev && prev.id === shotId ? { ...prev, mark: next ?? undefined } : prev));
      try {
        await api.markShot(shotId, next);
      } catch (err) {
        apply(previous);
        toast(err instanceof Error ? err.message : t('roll.markError'), 'error');
      }
    },
    [detail, toast, t],
  );

  /*
   * Approving a reference: the frames waiting on it are shot from it. Their
   * progress comes in on the roll's events, like any other frame's.
   */
  const [approving, setApproving] = useState<string | undefined>();
  const approve = useCallback(
    async (shotId: string) => {
      setApproving(shotId);
      try {
        const next = await api.approveShot(shotId);
        setDetail((prev) =>
          prev ? { ...prev, shots: prev.shots.map((s) => (s.id === next.id ? next : s)) } : prev,
        );
        setInspecting((prev) => (prev && prev.id === next.id ? next : prev));
        toast(t('roll.referenceApproved'), 'ok');
        void load();
      } catch (err) {
        toast(describe(err, 'roll.approveError'), 'error');
      } finally {
        setApproving(undefined);
      }
    },
    [describe, load, toast, t],
  );

  const shoot = detail?.shoot;
  const shots = useMemo(() => detail?.shots ?? [], [detail]);
  /*
   * A roll of poses is shot in two passes: the first pose in each place is
   * its reference, and the other poses there wait until it is approved.
   */
  const following = useMemo(() => {
    const map = new Map<string, number>();
    for (const shot of shots)
      if (shot.referenceShotId) map.set(shot.referenceShotId, (map.get(shot.referenceShotId) ?? 0) + 1);
    return map;
  }, [shots]);
  const referenceOf = (shot: Shot) =>
    following.has(shot.id)
      ? { approved: Boolean(shot.approved), following: following.get(shot.id)! }
      : undefined;
  const waitsForReference = (shot: Shot) =>
    Boolean(shot.referenceShotId) &&
    shot.status === 'pending' &&
    !shots.find((s) => s.id === shot.referenceShotId)?.approved;
  // A reference the judge passed is approved on its own; only one it turned down waits for the studio.
  const toApprove = shots.filter(
    (s) => following.has(s.id) && !s.approved && (s.status === 'done' || s.status === 'failed'),
  );
  const waiting = shots.filter(waitsForReference).length;
  const cost = detail?.cost;
  const done = shots.filter((s) => s.status === 'done');
  const active = shoot ? ACTIVE.has(shoot.status) : false;

  const byPov = useMemo(() => {
    const map = new Map<string, Shot[]>();
    for (const shot of shots) map.set(shot.povId, [...(map.get(shot.povId) ?? []), shot]);
    return [...map.entries()];
  }, [shots]);

  /*
   * The number printed on each frame of the sheet, by shot id. The continuity
   * note arrives in shot ids and has to point at a frame the reader can find,
   * so it is numbered the same way the sheet below numbers itself.
   */
  const frameNumbers = useMemo(() => {
    const numbers = new Map<string, number>();
    let n = 0;
    for (const [, povShots] of byPov) {
      for (const shot of [...povShots].sort((a, b) => a.variantIndex - b.variantIndex)) {
        numbers.set(shot.id, ++n);
      }
    }
    return numbers;
  }, [byPov]);

  if (error) return <div className="app__inner banner banner--error">{error}</div>;
  if (!shoot) {
    return (
      <div className="app__inner stack">
        <div className="skeleton" style={{ height: 108, aspectRatio: 'auto' }} />
        <Skeleton count={6} />
      </div>
    );
  }

  let frame = 0;

  return (
    <div className="app__inner stack sview">
      <Link to="/shoots" className="btn btn--ghost btn--sm" style={{ alignSelf: 'flex-start' }}>
        <BackIcon /> {t('common.back')}
      </Link>

      {/*
       * The slate: what this roll is on the left, in the page's largest type,
       * and on the right the count of frames ready, as large as the title —
       * the number you came to watch.
       */}
      <header className="shead">
        <div className="shead__main">
          <span className="shead__tag">
            {t(`rolls.mode.${shoot.settings.mode ?? 'presets'}`)} · {t(`enum.status.${shoot.status}`)}
          </span>
          <Display text={shoot.name} mark="none" className="shead__title" />
          <dl className="shead__specs">
            <div>
              <dt>{t('roll.setUps')}</dt>
              <dd>{shoot.povIds.length}</dd>
            </div>
            <div>
              <dt>{t('roll.format')}</dt>
              <dd>{shoot.settings.aspectRatio}</dd>
            </div>
            {/* What the roll has cost so far — the number the plan limits stand in for. */}
            {cost && cost.costMicros > 0 && (
              <div
                title={t('roll.costHint', {
                  render: usd(cost.byStage.render ?? 0),
                  critique: usd(cost.byStage.critique ?? 0),
                  setUp: usd(
                    (cost.byStage.styling ?? 0) +
                      (cost.byStage.prompting ?? 0) +
                      (cost.byStage['garment-analysis'] ?? 0),
                  ),
                })}
              >
                <dt>{t('roll.cost')}</dt>
                <dd>{usd(cost.costMicros)}</dd>
              </div>
            )}
          </dl>
        </div>
        {shots.length > 0 && (
          <div className="counter">
            <div className="counter__figure">
              <span key={done.length} className="counter__done">
                {String(done.length).padStart(2, '0')}
              </span>
              <span className="counter__total">/{String(shots.length).padStart(2, '0')}</span>
            </div>
            <span className="counter__label">
              {active && <span className="counter__live" aria-hidden="true" />}
              {t('roll.ready')}
            </span>
          </div>
        )}
      </header>

      {shoot.status === 'failed' && (
        <div className="banner banner--error">{shoot.error ?? t('roll.failed')}</div>
      )}

      {/*
       * The two passes of a roll of poses, said once above the frames: what
       * to approve, and what follows from it.
       */}
      {toApprove.length > 0 && waiting > 0 && (
        <div className="banner banner--reference">
          <b>{t('roll.referenceTitle')}</b>
          <span>{t(waiting === 1 ? 'roll.referenceHintOne' : 'roll.referenceHint', { n: waiting })}</span>
        </div>
      )}

      {/* The frames first: they are what the page is for. What the roll
          was made from — the look, the photograph, the brief — follows. */}
      {byPov.map(([povId, povShots]) => (
        <section key={povId} className="stack stack--sm">
          <div className="row row--between">
            <span className="label">{tp(`pov.${povId}.name`, findPov(povId)?.name ?? povId)}</span>
            <span className="mono" style={{ color: 'var(--ink-3)' }}>
              {povShots.filter((s) => s.status === 'done').length}/{povShots.length}
            </span>
          </div>
          <div className="sheet-grid sheet-grid--wide">
            {povShots
              .slice()
              .sort((a, b) => a.variantIndex - b.variantIndex)
              .map((shot) => (
                <ShotFrame
                  key={shot.id}
                  shot={shot}
                  frameNo={++frame}
                  reference={referenceOf(shot)}
                  waiting={waitsForReference(shot)}
                  approving={approving === shot.id}
                  onApprove={() => void approve(shot.id)}
                  onInspect={() => setInspecting(shot)}
                  onMark={(next) => void mark(shot.id, next)}
                  t={t}
                />
              ))}
          </div>
        </section>
      ))}

      {/*
       * How the roll reads as a series: one line, then each frame that is out
       * of step with the rest, shown beside its own picture so nobody has to
       * count along the sheet to find "frame 2". Written in the studio's
       * language when the judge was asked for it.
       */}
      {shoot.continuity && !shoot.continuity.consistent && (
        <section className="drift" aria-labelledby="drift-title">
          <div className="drift__head">
            <h2 id="drift-title" className="drift__title">
              {t('roll.continuity')}
            </h2>
            <span className="drift__count">
              {t(shoot.continuity.drift.length === 1 ? 'roll.driftCountOne' : 'roll.driftCount', {
                n: shoot.continuity.drift.length,
              })}
            </span>
          </div>
          <p className="drift__note">
            {(locale !== 'en' && shoot.continuity.noteLocal) || shoot.continuity.note}
          </p>
          <ul className="drift__list">
            {shoot.continuity.drift.map((drift) => {
              const frameShot = shots.find((s) => s.id === drift.shotId);
              return (
                <li key={drift.shotId} className={`drift__item is-${drift.severity}`}>
                  <span className="drift__thumb" aria-hidden="true">
                    {frameShot?.assetId && <img src={assetUrl(frameShot.assetId)} alt="" loading="lazy" />}
                  </span>
                  <span className="drift__body">
                    <span className="drift__meta">
                      <b>{t('roll.driftFrame', { n: frameNumbers.get(drift.shotId) ?? 0 })}</b>
                      <span className="drift__sev">{t(`roll.drift.${drift.severity}`)}</span>
                    </span>
                    <span className="drift__text">
                      {(locale !== 'en' && drift.issueLocal) || drift.issue}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {shoot.settings.mode === 'copy' && shoot.settings.referenceAssetId && (
        <div className="panel copyRef">
          <img src={assetUrl(shoot.settings.referenceAssetId)} alt="" />
          <div className="stack stack--sm">
            <span className="label">{t('roll.recreated')}</span>
            <p className="faint">
              {t(shoot.settings.keepReferenceScene ? 'roll.recreatedKept' : 'roll.recreatedMoved')}
            </p>
          </div>
        </div>
      )}

      {shoot.settings.mode === 'brief' && shoot.settings.brief && (
        <BriefPanel
          shootId={shoot.id}
          brief={shoot.settings.brief}
          rationale={shoot.settings.briefRationale}
          feedback={shoot.settings.briefFeedback ?? []}
          parentShootId={shoot.settings.parentShootId}
          busy={active}
        />
      )}

      {events.length > 0 && (
        <details className="panel">
          <summary className="label" style={{ cursor: 'pointer' }}>
            {t('roll.log')}
          </summary>
          <div className="log" style={{ marginTop: 14 }}>
            {events
              .slice()
              .reverse()
              .map((event, index) => (
                <div key={index} className={`log__line log__line--${event.level}`}>
                  <span className="log__time">
                    {new Date(event.at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </span>
                  <span>{event.message}</span>
                </div>
              ))}
          </div>
        </details>
      )}

      {/*
       * The dock: where the roll stands and what can be done with it, held at
       * the foot of the screen in a black pill so it is there wherever the
       * sheet has been scrolled to.
       */}
      <div className="sdock" role="status">
        <span className="sdock__text">
          {active ? (
            <>
              <span className="livedot" aria-hidden="true" />
              <span className="sdock__msg">{events[events.length - 1]?.message ?? t('roll.working')}</span>
              <b className="mono">{Math.round(progress * 100)} %</b>
            </>
          ) : (
            <b>{t('roll.dockDone', { n: done.length })}</b>
          )}
        </span>
        {active && (
          <span className="sdock__bar" aria-hidden="true">
            <span style={{ width: `${progress * 100}%` }} />
          </span>
        )}
        <span className="sdock__actions">
          {active && (
            <button
              className="btn btn--sm sdock__dark"
              onClick={async () => {
                await api.cancelShoot(shoot.id);
                void load();
              }}
            >
              {t('roll.cut')}
            </button>
          )}
          {/* The recipe of a roll that worked, kept for the next drop. The
              garments are left out of it on purpose — see the preset type. */}
          <button
            className="btn btn--sm sdock__light"
            onClick={async () => {
              const label = await prompt({
                title: t('shoot.recipeName'),
                label: t('shoot.recipeNameLabel'),
                value: shoot.name,
                confirmLabel: t('common.save'),
              });
              if (!label?.trim()) return;
              try {
                const saved = await api.createShootPreset({
                  name: label.trim(),
                  settings: shoot.settings,
                  fromShootId: shoot.id,
                });
                toast(t('shoot.recipeSaved', { name: saved.name }), 'ok');
              } catch (err) {
                toast(describe(err, 'roll.notLoaded'), 'error');
              }
            }}
          >
            {t('roll.saveRecipe')}
          </button>
        </span>
      </div>

      {inspecting && (
        <LightBox
          shot={inspecting}
          garments={detail?.garments ?? []}
          identityAssetIds={detail?.identityAssetIds ?? []}
          pose={shoot.settings.poseRefs?.find((ref) => ref.id === inspecting.poseRefId)}
          scene={shoot.settings.sceneRefs?.find((ref) => ref.id === inspecting.poseRefId)}
          frameNo={shots.findIndex((s) => s.id === inspecting.id) + 1}
          stockId={shoot.settings.filmStockId}
          stockGrain={shoot.settings.grain}
          stockVignette={shoot.settings.vignette ?? 0}
          onMark={(next) => void mark(inspecting.id, next)}
          reference={referenceOf(inspecting)}
          approving={approving === inspecting.id}
          onApprove={() => void approve(inspecting.id)}
          t={t}
          tp={tp}
          onReshot={(next) => {
            setInspecting(next);
            setDetail((prev) =>
              prev ? { ...prev, shots: prev.shots.map((s) => (s.id === next.id ? next : s)) } : prev,
            );
            toast(t('roll.updated'), 'ok');
          }}
          onClose={() => setInspecting(undefined)}
        />
      )}
    </div>
  );
}

/**
 * Making a link, and seeing what came back through it.
 *
 * The URL is shown exactly once, at the moment it is made, because the server
 * keeps only an HMAC of the token — the same treatment as a session or a
 * reset link, and for the same reason: a copy of the database should not be a
 * set of working links to unreleased collections. Losing one costs a new
 * link, which revokes the old.
 */
/**
 * The brief a roll was planned from, and the way to a better version: say
 * what is not right, and the studio plans it again with that and everything
 * said before, and shoots the new version.
 */
function BriefPanel({
  shootId,
  brief,
  rationale,
  feedback,
  parentShootId,
  busy,
}: {
  shootId: string;
  brief: string;
  rationale?: string;
  feedback: string[];
  parentShootId?: string;
  busy: boolean;
}) {
  const t = useT();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const describe = useErrorText();
  const toast = useToast();
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const revise = async () => {
    setSending(true);
    setError('');
    try {
      const { shoot } = await api.reviseShoot(shootId, note.trim(), locale === 'en' ? 'en' : 'fr');
      toast(t('brief.revised', { name: shoot.name }), 'ok');
      setNote('');
      navigate(`/shoots/${shoot.id}`);
    } catch (err) {
      setError(describe(err, 'brief.reviseFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="panel stack stack--sm">
      <div className="row row--between">
        <span className="label">{t('roll.brief')}</span>
        {parentShootId && (
          <Link className="btn btn--link btn--sm" to={`/shoots/${parentShootId}`}>
            {t('roll.previousVersion')}
          </Link>
        )}
      </div>
      <p>{brief}</p>
      {rationale && <p className="muted">{rationale}</p>}
      {feedback.length > 0 && (
        <ol className="faint briefNotes">
          {feedback.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ol>
      )}
      <label className="label" htmlFor={`revise-${shootId}`}>
        {t('roll.reviseLabel')}
      </label>
      <textarea
        id={`revise-${shootId}`}
        className="textarea"
        rows={2}
        value={note}
        placeholder={t('roll.revisePlaceholder')}
        onChange={(event) => setNote(event.target.value)}
      />
      {error && <div className="banner banner--error">{error}</div>}
      <button
        type="button"
        className="btn btn--primary"
        style={{ alignSelf: 'flex-start' }}
        disabled={sending || busy || note.trim().length < 2}
        onClick={() => void revise()}
      >
        {sending ? <span className="spinner" /> : null}
        {t(sending ? 'roll.revising' : 'roll.revise')}
      </button>
      {busy && <p className="faint">{t('roll.reviseWait')}</p>}
    </div>
  );
}

type Phase = 'queued' | 'working' | 'checking' | 'failed';

function phaseOf(status: Shot['status']): Phase {
  if (status === 'failed') return 'failed';
  if (status === 'pending') return 'queued';
  if (status === 'reviewing') return 'checking';
  return 'working';
}

const PHASE_LABEL: Record<Phase, string> = {
  queued: 'roll.queuedFrame',
  working: 'roll.developingFrame',
  checking: 'roll.checkingFrame',
  failed: 'roll.frameFailed',
};

/** A place's reference in a roll of poses: approved or not, and how many frames are drawn from it. */
type ReferenceState = { approved: boolean; following: number };

function ShotFrame({
  shot,
  frameNo,
  reference,
  waiting,
  approving,
  onApprove,
  onInspect,
  onMark,
  t,
}: {
  shot: Shot;
  frameNo: number;
  /** When this frame is its place's reference. */
  reference?: ReferenceState;
  /** When this frame waits for its reference to be approved. */
  waiting?: boolean;
  approving?: boolean;
  onApprove: () => void;
  onInspect: () => void;
  onMark: (next: ShotMark | null) => void;
  t: Translate;
}) {
  const tp = usePresetLabel();
  const score = shot.critique?.score;
  // The pose's name in the interface's language, never the words the model was given.
  const pose = shot.poseId
    ? tp(`pose.${shot.poseId}.name`, findPose(shot.poseId)?.name ?? '') || undefined
    : undefined;
  const phase = phaseOf(shot.status);

  return (
    <div className={`frame frame--markable ${shot.mark ? `is-${shot.mark}` : ''}`}>
      <button
        type="button"
        className="frame__open"
        onClick={onInspect}
        aria-label={t('roll.inspect', { n: frameNo })}
      >
        {shot.assetId ? (
          <img className="frame__img" src={assetUrl(shot.assetId)} alt={shot.povId} loading="lazy" />
        ) : (
          /*
           * A frame not yet made says which stage it is at: waiting its turn
           * (an outline), being rendered (ink rising in it), or being
           * checked by the critic (a line passing down it).
           */
          <span className={`frame__placeholder is-${phase}`}>
            {phase === 'working' && <span className="frame__fill" aria-hidden="true" />}
            {phase === 'checking' && <span className="frame__scan" aria-hidden="true" />}
            <span className="frame__state">{t(waiting ? 'roll.waitsReference' : PHASE_LABEL[phase])}</span>
          </span>
        )}
        <span className="frame__num">{String(frameNo).padStart(2, '0')}</span>
        {reference && (
          <span className={`frame__ref ${reference.approved ? 'is-approved' : ''}`}>
            {t(reference.approved ? 'roll.referenceApprovedBadge' : 'roll.referenceBadge')}
          </span>
        )}
        {score !== undefined && (
          <span className={`frame__score ${score >= 80 ? 'is-ok' : score >= 60 ? 'is-warn' : 'is-bad'}`}>
            {score}
          </span>
        )}
        <span className="frame__loupe" aria-hidden="true">
          <LoupeIcon />
        </span>
      </button>

      {/* The pose by name when it has one, then the decision across the card. */}
      <div className="frame__foot">
        {pose && <span className="frame__pose">{pose}</span>}
        {shot.assetId && <MarkStrip mark={shot.mark} onMark={onMark} size="sm" />}
        {reference && !reference.approved && (
          <ApproveButton
            shot={shot}
            reference={reference}
            approving={approving}
            onApprove={onApprove}
            t={t}
            small
          />
        )}
      </div>
    </div>
  );
}

/**
 * Approving a place's reference: her face and the clothes are right, so the
 * other poses are drawn from it. Closed, with the reason on it, until the
 * reference is finished.
 */
function ApproveButton({
  shot,
  reference,
  approving,
  onApprove,
  t,
  small,
}: {
  shot: Shot;
  reference: ReferenceState;
  approving?: boolean;
  onApprove: () => void;
  t: Translate;
  small?: boolean;
}) {
  const ready = shot.status === 'done' && Boolean(shot.assetId);
  return (
    <button
      type="button"
      className={`btn btn--primary ${small ? 'btn--sm' : ''}`}
      onClick={onApprove}
      disabled={!ready || approving}
      title={ready ? undefined : t('roll.approveNotReady')}
    >
      {t(
        approving
          ? 'roll.approving'
          : reference.following === 1
            ? 'roll.approveReferenceOne'
            : 'roll.approveReference',
        { n: reference.following },
      )}
    </button>
  );
}

/**
 * Direction A: reference versus render, under a wipe.
 *
 * The critic scores garment fidelity by comparing the render to your garment
 * photo — but until now you never saw that comparison, only the number. Drag
 * the handle and the verdict becomes checkable: when the score says the sequins
 * came out round, you can see that they did.
 *
 * And when they did, the critic has already written the correction. It is
 * editable here and reshoots this one frame, so a frame that came out at 68
 * after its last automatic retry is no longer simply stuck at 68.
 */
function LightBox({
  shot,
  garments,
  identityAssetIds,
  pose: posePhoto,
  scene,
  frameNo,
  stockId,
  stockGrain,
  stockVignette,
  onMark,
  reference,
  approving,
  onApprove,
  onReshot,
  onClose,
  t,
  tp,
}: {
  shot: Shot;
  garments: Garment[];
  identityAssetIds: string[];
  /** The pose photograph this frame reproduces, in a roll of poses. */
  pose?: { name: string; assetId: string };
  /** The Studio scene this frame remakes, in a roll of scenes. */
  scene?: { name: string; assetId: string };
  frameNo: number;
  stockId: string;
  stockGrain: number;
  stockVignette: number;
  onMark: (next: ShotMark | null) => void;
  /** When this frame is its place's reference. */
  reference?: ReferenceState;
  approving?: boolean;
  onApprove: () => void;
  onReshot: (next: Shot) => void;
  onClose: () => void;
  t: Translate;
  tp: (key: string, fallback: string) => string;
}) {
  const describe = useErrorText();
  const date = useDateFormat();
  const { locale } = useI18n();
  const pov = findPov(shot.povId);
  const povName = tp(`pov.${shot.povId}.name`, pov?.name ?? shot.povId);
  const [compare, setCompare] = useState<string | undefined>();
  /** An earlier take being looked at instead of the picture on show. */
  const [viewing, setViewing] = useState<string | undefined>();
  const [restoring, setRestoring] = useState(false);
  const [wipe, setWipe] = useState(58);
  const [correction, setCorrection] = useState(
    shot.corrections?.length ? '' : (shot.critique?.suggestion ?? ''),
  );
  /** The corrections already applied to this frame that the next reshoot keeps. */
  const [kept, setKept] = useState<string[]>(shot.corrections ?? []);
  useEffect(() => setKept(shot.corrections ?? []), [shot.corrections]);
  const [reshooting, setReshooting] = useState(false);
  const [reshootError, setReshootError] = useState('');
  const [stock, setStock] = useState(stockId);
  const [grain, setGrain] = useState(stockGrain);
  const [vignette, setVignette] = useState(stockVignette);
  const [developing, setDeveloping] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  const references = useMemo(
    () => [
      ...garments.map((garment, i) => ({
        assetId: garment.assetId,
        label: `IMG ${String(i + 1).padStart(2, '0')} ${garment.label}`,
        kind: 'garment' as const,
      })),
      ...identityAssetIds.map((assetId, i) => ({
        assetId,
        label: identityAssetIds.length > 1 ? `${t('loupe.identity')} ${i + 1}` : t('loupe.identity'),
        kind: 'identity' as const,
      })),
      ...(posePhoto ? [{ assetId: posePhoto.assetId, label: t('loupe.pose'), kind: 'pose' as const }] : []),
      ...(scene ? [{ assetId: scene.assetId, label: t('loupe.scene'), kind: 'pose' as const }] : []),
    ],
    [garments, identityAssetIds, posePhoto, scene, t],
  );

  const against = references.find((ref) => ref.assetId === compare);

  /*
   * While the render loop still owns a frame, both of these would be
   * overwritten by its own next write — so they are closed, with the reason
   * on the button rather than in an error after the fact.
   */
  const settled = shot.status === 'done' || shot.status === 'failed';

  /* One handler for mouse, pen and touch — pointer events cover all three. */
  const dragTo = (clientX: number) => {
    const box = stageRef.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    setWipe(Math.max(0, Math.min(100, ((clientX - box.left) / box.width) * 100)));
  };

  /*
   * Redeveloping works from the negative kept when the frame was first
   * developed, so trying a grain costs milliseconds rather than a render.
   */
  const redevelop = async () => {
    setDeveloping(true);
    setReshootError('');
    try {
      onReshot(await api.develop(shot.id, stock, grain, vignette));
    } catch (err) {
      setReshootError(describe(err, 'loupe.developError'));
    } finally {
      setDeveloping(false);
    }
  };

  // A new picture on show (a reshoot, a restore) ends any look back.
  useEffect(() => setViewing(undefined), [shot.assetId]);

  /* Newest first: the history is read to find what came before. */
  const takes = useMemo(() => [...(shot.takes ?? [])].reverse(), [shot.takes]);
  const looking = takes.find((take) => take.assetId === viewing && take.assetId !== shot.assetId);
  const shown = looking?.assetId ?? shot.assetId;

  const restore = async (assetId: string) => {
    setRestoring(true);
    setReshootError('');
    try {
      onReshot(await api.restoreTake(shot.id, assetId));
    } catch (err) {
      setReshootError(describe(err, 'loupe.restoreFailed'));
    } finally {
      setRestoring(false);
    }
  };

  /** A scene's frame: only its variation again, from the scene's edit — the step that comes out right. */
  const vary = async () => {
    setReshooting(true);
    setReshootError('');
    try {
      onReshot(await api.vary(shot.id, shown));
    } catch (err) {
      setReshootError(describe(err, 'loupe.varyFailed'));
    } finally {
      setReshooting(false);
    }
  };

  const reshoot = async () => {
    setReshooting(true);
    setReshootError('');
    try {
      // Written, it corrects the version being looked at; blank, the frame is drawn again.
      onReshot(await api.reshoot(shot.id, correction, kept, correction.trim() ? shown : undefined));
      setCorrection('');
    } catch (err) {
      setReshootError(describe(err, 'loupe.reshootFailed'));
    } finally {
      setReshooting(false);
    }
  };

  const issues =
    (locale !== 'en' && shot.critique?.issuesLocal?.length && shot.critique.issuesLocal) ||
    shot.critique?.issues ||
    [];
  // A frame from a pose photograph is named after that photograph, not after the set-up.
  const pose = shot.poseId
    ? tp(`pose.${shot.poseId}.name`, findPose(shot.poseId)?.name ?? shot.poseId)
    : posePhoto?.name || t('loupe.exp.defaultPose');

  /*
   * The picture as large as the screen allows on the left; on the right, a
   * column read top to bottom in the order the decision is made: which frame
   * this is, how the judge scored it and what it found, keep or cut, take it
   * away. Developing and reshooting are folded under that — they are what is
   * done to a frame that is not right yet, not what every frame needs.
   */
  return (
    <Sheet title={t('roll.driftFrame', { n: String(frameNo).padStart(2, '0') })} onClose={onClose} wide>
      <div className="lb">
        <div className="lb__stage">
          <div
            ref={stageRef}
            className={`wipe lb__wipe ${against ? 'is-split' : ''}`}
            onPointerDown={(event) => {
              if (!against) return;
              event.currentTarget.setPointerCapture(event.pointerId);
              dragTo(event.clientX);
            }}
            onPointerMove={(event) => {
              if (!against || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
              dragTo(event.clientX);
            }}
          >
            {shown && <img className="wipe__base" src={assetUrl(shown)} alt="" />}

            {against && (
              <>
                {/* The reference, revealed to the right of the handle. */}
                <div className="wipe__over" style={{ clipPath: `inset(0 0 0 ${wipe}%)` }}>
                  <img src={assetUrl(against.assetId)} alt={against.label} />
                </div>
                <div className="wipe__line" style={{ left: `${wipe}%` }}>
                  <span
                    className="wipe__grip"
                    role="slider"
                    tabIndex={0}
                    aria-label={t('loupe.wipe')}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(wipe)}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowLeft') setWipe((v) => Math.max(0, v - 4));
                      if (event.key === 'ArrowRight') setWipe((v) => Math.min(100, v + 4));
                    }}
                  >
                    ↔
                  </span>
                </div>
                <span className="wipe__tag wipe__tag--left">{t('loupe.render')}</span>
                <span className="wipe__tag wipe__tag--right">{t('loupe.reference')}</span>
              </>
            )}
          </div>

          {/* Looking back at an earlier version: say so on the picture, with the way back. */}
          {looking && (
            <div className="lb__looking">
              <span>
                {t('loupe.takeViewing', {
                  origin: t(`loupe.take.${looking.origin}`),
                  time: date.stamp(looking.at),
                })}
              </span>
              <span className="lb__lookingActs">
                <button type="button" className="btn btn--sm" onClick={() => setViewing(undefined)}>
                  {t('loupe.backToCurrent')}
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={restoring || !settled}
                  onClick={() => void restore(looking.assetId)}
                >
                  {restoring ? <span className="spinner" /> : null}
                  {t('loupe.restoreTake')}
                </button>
              </span>
            </div>
          )}

          {/* Compare against a garment or the face, under the picture. */}
          {references.length > 0 && shot.assetId && (
            <div className="lb__refs">
              <button
                type="button"
                className={`chip ${against ? '' : 'is-on'}`}
                aria-pressed={!against}
                onClick={() => setCompare(undefined)}
              >
                {t('loupe.frameOnly')}
              </button>
              {references.map((ref) => (
                <button
                  key={ref.assetId}
                  type="button"
                  className={`chip ${compare === ref.assetId ? 'is-on' : ''}`}
                  aria-pressed={compare === ref.assetId}
                  onClick={() => setCompare((prev) => (prev === ref.assetId ? undefined : ref.assetId))}
                >
                  <img className="chip__thumb" src={assetUrl(ref.assetId)} alt="" loading="lazy" />
                  {ref.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <aside className="lb__side">
          {shot.error && <div className="banner banner--error">{shot.error}</div>}

          <div className="lb__head">
            <h3 className="lb__pose">{pose}</h3>
            <div className="lb__tags">
              <span>{povName}</span>
              <span>{t(shot.attempt === 1 ? 'loupe.takesOne' : 'loupe.takesN', { n: shot.attempt })}</span>
            </div>
          </div>

          {shot.critique && (
            <section className="lb__score" aria-label={t('loupe.quality')}>
              <div className="lb__figure">
                <b className="mono">{shot.critique.score}</b>
                <span>/100</span>
              </div>
              <div className="lb__meters">
                <Meter label={t('loupe.garmentFidelity')} value={shot.critique.garmentFidelity} />
                <Meter label={t('loupe.identity')} value={shot.critique.identityConsistency} />
                <Meter label={t('loupe.realism')} value={shot.critique.realism} />
              </div>
            </section>
          )}

          {/* A reference is approved here as well as on its card, once it has been looked at closely. */}
          {reference && (
            <div className="lb__reference">
              {reference.approved ? (
                <p className="faint">{t('loupe.referenceApproved')}</p>
              ) : (
                <>
                  <p className="faint">{t('loupe.referenceHint')}</p>
                  <ApproveButton
                    shot={shot}
                    reference={reference}
                    approving={approving}
                    onApprove={onApprove}
                    t={t}
                  />
                </>
              )}
            </div>
          )}

          {shot.assetId && (
            <div className="lb__decide">
              <MarkStrip mark={shot.mark} onMark={onMark} />
              {/*
               * Two sizes, said in words: the file as it was rendered, or
               * twice the size and sharpened for a printed page.
               */}
              <div className="lb__download">
                <a className="btn btn--primary" href={downloadUrl(shot.assetId)} download>
                  <DownloadIcon /> {t('loupe.downloadOriginal')}
                </a>
                <a className="btn" href={downloadUrl(shot.assetId, '2x')} download>
                  <DownloadIcon /> {t('loupe.downloadPrint')}
                </a>
              </div>
              <p className="faint lb__hint">{t('loupe.downloadHint')}</p>
            </div>
          )}

          {/*
           * Every picture this frame has been — first render, retries,
           * repairs, reshoots — as small whole thumbnails beside the photo,
           * so the one on show stays whole. Pressing one shows it large.
           */}
          {takes.length > 1 && (
            <section className="lb__versions" aria-label={t('loupe.history')}>
              <span className="label">{t('loupe.historyCount', { n: takes.length })}</span>
              <div className="lb__versionGrid" role="list">
                {takes.map((take, index) => {
                  const current = take.assetId === shot.assetId;
                  const on = current ? !looking : looking?.assetId === take.assetId;
                  return (
                    <button
                      key={take.assetId}
                      type="button"
                      role="listitem"
                      className={`lb__version ${on ? 'is-on' : ''}`}
                      aria-pressed={on}
                      title={current ? t('loupe.takeCurrent') : t(`loupe.take.${take.origin}`)}
                      onClick={() => setViewing(current ? undefined : take.assetId)}
                    >
                      <img src={assetUrl(take.assetId)} alt="" loading="lazy" />
                      <span className="lb__versionMeta">
                        <b>#{takes.length - index}</b>
                        {take.critique && <span>{take.critique.score}</span>}
                      </span>
                      {current && <span className="lb__versionNow">{t('loupe.takeCurrentShort')}</span>}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {issues.length > 0 && (
            <section className="lb__issues">
              <span className="label">{t('loupe.toFix')}</span>
              <ul>
                {issues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
              {shot.attempt > 1 && <p className="faint">{t('loupe.reshotTimes', { n: shot.attempt - 1 })}</p>}
            </section>
          )}

          {scene && (
            <section className="lb__vary">
              <button
                type="button"
                className="btn btn--block"
                disabled={reshooting || !settled}
                onClick={() => void vary()}
              >
                {reshooting ? <span className="spinner" /> : null}
                {t(reshooting ? 'loupe.reshooting' : 'loupe.vary')}
              </button>
              <p className="faint">{t('loupe.varyHint')}</p>
            </section>
          )}

          {/* The critic already wrote the fix; this is where it gets used. */}
          <details className="lb__fold">
            <summary>{t('loupe.fix')}</summary>
            <div className="lb__foldBody">
              <p className="faint">{t('loupe.fromReferences')}</p>
              {kept.length > 0 && (
                <ol className="lb__fixes" aria-label={t('loupe.fixesSoFar')}>
                  {kept.map((text, index) => (
                    <li key={`${index}-${text}`}>
                      <span>{text}</span>
                      <button
                        type="button"
                        className="lb__fixDrop"
                        aria-label={t('loupe.fixDrop', { text })}
                        title={t('loupe.fixDrop', { text })}
                        onClick={() => setKept((list) => list.filter((_, i) => i !== index))}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ol>
              )}
              <textarea
                className="textarea"
                rows={3}
                aria-label={t(kept.length ? 'loupe.fixAnother' : 'loupe.fix')}
                value={correction}
                placeholder={t('loupe.fixPlaceholder')}
                onChange={(event) => setCorrection(event.target.value)}
              />
              {reshootError && <div className="banner banner--error">{reshootError}</div>}
              <button
                className="btn btn--primary btn--block"
                disabled={reshooting || !settled}
                onClick={() => void reshoot()}
              >
                {reshooting ? <span className="spinner" /> : null}
                {reshooting
                  ? t('loupe.reshooting')
                  : !settled
                    ? t('loupe.stillShooting')
                    : correction.trim()
                      ? t(looking ? 'loupe.retouchThat' : 'loupe.retouch')
                      : t('loupe.reshoot', { n: String(frameNo).padStart(2, '0') })}
              </button>
            </div>
          </details>

          {/* Development is reversible, so it belongs here rather than only at set-up. */}
          {shot.assetId && (
            <details className="lb__fold">
              <summary>
                {t('loupe.develop')}
                <span className="faint">
                  {t(shot.negativeAssetId ? 'loupe.negativeOnFile' : 'loupe.undeveloped')}
                </span>
              </summary>
              <div className="lb__foldBody">
                <div className="refbar">
                  {FILM_STOCKS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`chip ${stock === option.id ? 'is-on' : ''}`}
                      aria-pressed={stock === option.id}
                      onClick={() => {
                        setStock(option.id);
                        setGrain(option.grain);
                      }}
                    >
                      {tp(`stock.${option.id}.name`, option.name)}
                    </button>
                  ))}
                </div>
                {stock !== NO_STOCK_ID && (
                  <div className="dial">
                    <label className="dial__label" htmlFor={`grain-${shot.id}`}>
                      {t('shoot.grain')}
                    </label>
                    <input
                      id={`grain-${shot.id}`}
                      className="dial__range"
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={grain}
                      onChange={(event) => setGrain(Number(event.target.value))}
                    />
                    <span className="dial__value mono">{Math.round(grain)}</span>
                  </div>
                )}
                <Dial
                  id={`vignette-${shot.id}`}
                  label={t('shoot.vignette')}
                  value={vignette}
                  onChange={setVignette}
                />
                <button
                  className="btn btn--block"
                  disabled={developing || shot.status !== 'done'}
                  onClick={() => void redevelop()}
                >
                  {developing ? <span className="spinner" /> : null}
                  {t(developing ? 'loupe.developing' : 'loupe.developFrame')}
                </button>
              </div>
            </details>
          )}

          <SentToGemini shotId={shot.id} assetId={viewing ?? shot.assetId} t={t} />
          <PublishScene shot={shot} t={t} />
        </aside>
      </div>
    </Sheet>
  );
}

function meterColor(value: number): string {
  return value >= 80 ? 'var(--green)' : value >= 60 ? 'var(--safelight)' : 'var(--red)';
}

function Meter({ label, value }: { label: string; value: number }) {
  return (
    <div className="meter">
      <span className="meter__label">{label}</span>
      <span className="meter__track">
        <span className="meter__fill" style={{ width: `${value}%`, background: meterColor(value) }} />
      </span>
      <span className="meter__value">{value}</span>
    </div>
  );
}
